/**
 * Mock e-ticket generator.
 *
 * Builds a minimal but valid single-page PDF (Helvetica text) entirely client-side —
 * no external library — so the Confirmation page's "Download Ticket" button produces a
 * real .pdf file. Content is ASCII, which keeps byte offsets == string lengths so the
 * xref table stays correct.
 */

import { formatMoney } from "./fare";

/** Escape a string for use inside a PDF text-showing operator. */
const escapePdf = (s) =>
  String(s).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");

/** Replace any non-ASCII characters so the PDF stays single-byte. */
const asciiOnly = (s) => String(s).replace(/[^\x20-\x7E]/g, "?");

/**
 * Assemble a valid PDF document from an array of text lines.
 * @param {string[]} lines
 * @returns {string} Raw PDF bytes as an ASCII string.
 */
function buildPdf(lines) {
  const header = "%PDF-1.4\n";

  let text = "BT\n/F1 12 Tf\n50 800 Td\n16 TL\n";
  lines.forEach((ln, i) => {
    const safe = escapePdf(asciiOnly(ln));
    text += i === 0 ? `(${safe}) Tj\n` : `T* (${safe}) Tj\n`;
  });
  text += "ET";

  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >>",
    `<< /Length ${text.length} >>\nstream\n${text}\nendstream`,
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
  ];

  let body = "";
  const offsets = [];
  let pos = header.length;
  objects.forEach((o, i) => {
    const objStr = `${i + 1} 0 obj\n${o}\nendobj\n`;
    offsets.push(pos);
    body += objStr;
    pos += objStr.length;
  });

  const xrefStart = pos;
  let xref = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach((off) => {
    xref += `${String(off).padStart(10, "0")} 00000 n \n`;
  });

  const trailer = `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;
  return header + body + xref + trailer;
}

/**
 * Compose the ticket text lines from a confirmed booking.
 * @param {import('../mock/types').Booking} booking
 * @returns {string[]}
 */
function ticketLines(booking) {
  const { flight, passengers = [], contact = {}, fare = {}, reference, pnr } = booking;
  const lines = [
    "FALCON DESTINATIONS (PVT.) LTD",
    "Electronic Ticket / Itinerary Receipt",
    "----------------------------------------",
    `Booking Reference: ${reference}`,
    `PNR: ${pnr}`,
    `Status: ${booking.status || "CONFIRMED"}`,
    "",
    "FLIGHT DETAILS",
    `${flight.airline} (${flight.flightNumber})   ${flight.aircraft}`,
    `From: ${flight.departureAirport.city} (${flight.departureAirport.code})  Dep ${flight.departureTime}`,
    `To:   ${flight.arrivalAirport.city} (${flight.arrivalAirport.code})  Arr ${flight.arrivalTime}${
      flight.arrivalDayOffset > 0 ? ` (+${flight.arrivalDayOffset} day)` : ""
    }`,
    `Duration: ${flight.duration}   Stops: ${
      flight.stopCount === 0 ? "Nonstop" : `${flight.stopCount} (${flight.stopCities.join(", ")})`
    }`,
    `Cabin: ${flight.cabinClass}   Baggage: ${flight.baggageAllowance}   ${
      flight.refundable ? "Refundable" : "Non-refundable"
    }`,
    "",
    "PASSENGERS",
  ];

  passengers.forEach((p, i) => {
    lines.push(
      `${i + 1}. ${p.fullName}  (${p.type})  ${p.gender}  Nat: ${p.nationality}  Passport: ${p.passportNumber}`
    );
  });

  lines.push("");
  lines.push("CONTACT");
  lines.push(`Email: ${contact.email || "-"}   Phone: ${contact.phone || "-"}`);
  lines.push("");
  lines.push("FARE SUMMARY");
  lines.push(`Base Fare:   ${formatMoney(fare.baseFare || 0, fare.currency)}`);
  lines.push(`Taxes:       ${formatMoney(fare.taxes || 0, fare.currency)}`);
  lines.push(`Service Fee: ${formatMoney(fare.serviceFee || 0, fare.currency)}`);
  lines.push(`TOTAL PAID:  ${formatMoney(fare.total || 0, fare.currency)}`);
  lines.push("");
  lines.push("Thank you for booking with Falcon Destinations.");
  lines.push("This is a mock ticket generated for demonstration purposes.");
  return lines;
}

/**
 * Generate and trigger a download of the e-ticket PDF for a booking.
 * @param {import('../mock/types').Booking} booking
 */
export function downloadTicket(booking) {
  const pdf = buildPdf(ticketLines(booking));
  // Latin1-safe byte array (content is ASCII).
  const bytes = new Uint8Array(pdf.length);
  for (let i = 0; i < pdf.length; i++) bytes[i] = pdf.charCodeAt(i) & 0xff;
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `falcon-ticket-${booking.pnr}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
